/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {getLayoutFromLocalStorage, ILayout, LayoutSetup} from '../../../_metronic/layout/core'
import {ToolbarClassic} from '../../../_metronic/layout/components/toolbar/toolbars'
import {useAuth} from '../../modules/auth'
import {dummyClaimsData} from '../../../data/claims'
import {FaEdit} from 'react-icons/fa'
import axios from 'axios'
import moment from 'moment'
import styled from 'styled-components'

// Styled Components
const StyledCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: -15px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`

const SearchContainer = styled.div`
  display: flex;
  gap: 10px;
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
`

const StyledSelect = styled.select`
  width: 200px;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background-color: white;
  font-size: 14px;
  color: #212529;
  transition: all 0.2s ease;

  &:focus {
    border-color: #059bb6;
    box-shadow: 0 0 0 2px rgba(5, 155, 182, 0.1);
    outline: none;
  }
`

const StyledInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    border-color: #059bb6;
    box-shadow: 0 0 0 2px rgba(5, 155, 182, 0.1);
    outline: none;
  }
`

const TableContainer = styled.div`
  padding: 20px;
  overflow-x: auto;
`

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;

  th {
    background: #f8f9fa;
    padding: 12px;
    font-weight: 600;
    text-align: left;
    border-bottom: 2px solid #e9ecef;
  }

  td {
    padding: 12px;
    border-bottom: 1px solid #e9ecef;
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: #f8f9fa;
  }
`

const ActionButton = styled.button`
  background: #059bb6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #048299;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const StatusBadge = styled.span<{$status: string}>`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => {
    switch (props.$status) {
      case 'New Claim':
        return '#E8FFF3'
      case 'Registered':
        return '#F8F5FF'
      case 'GL Approved':
        return '#E1F0FF'
      case 'Cancelled':
        return '#FFF5F8'
      case 'Rejected':
        return '#FFE2E5'
      default:
        return '#F8F9FA'
    }
  }};
  color: ${(props) => {
    switch (props.$status) {
      case 'New Claim':
        return '#50CD89'
      case 'Registered':
        return '#7239EA'
      case 'GL Approved':
        return '#3699FF'
      case 'Cancelled':
        return '#F1416C'
      case 'Rejected':
        return '#F1416C'
      default:
        return '#A1A5B7'
    }
  }};
`

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #e9ecef;
`

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 14px;
`

const PaginationButtons = styled.div`
  display: flex;
  gap: 5px;

  button {
    padding: 6px 12px;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #f8f9fa;
      border-color: #059bb6;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.active {
      background: #059bb6;
      color: white;
      border-color: #059bb6;
    }
  }
`

interface Claim {
  id: string
  memberName: string
  claimNumber: string
  cardNumber: string
  policyNumber: string
  customerGroup: string
  status: string
  claimDate: string
}

interface SettlementPageProps {
  claimType: string
  searchType: string
  searchQuery: string
  onSearchTypeChange: (value: string) => void
  onSearchQueryChange: (value: string) => void
  currentPage: number
  onPageChange: (page: number) => void
}

const SettlementPage: React.FC<SettlementPageProps> = ({
  claimType,
  searchType,
  searchQuery,
  onSearchTypeChange,
  onSearchQueryChange,
  currentPage,
  onPageChange,
}) => {
  const navigate = useNavigate()
  const [setTab] = useState('Sidebar')
  const [config, setConfig] = useState<ILayout>(getLayoutFromLocalStorage())
  const [configLoading, setConfigLoading] = useState<boolean>(false)
  const [resetLoading, setResetLoading] = useState<boolean>(false)
  const [claims, setClaims] = useState<Claim[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const {currentUser} = useAuth()
  const auth = useAuth()
  const [pageSize, setPageSize] = useState(10)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  console.log('providerCode : ', currentUser?.providerCode)

  useEffect(() => {
    const fetchClaims = async () => {
      try {
        setLoading(true)
        setError(null)

        let queryParam = ''
        if (searchQuery) {
          switch (searchType) {
            case 'Claim Number':
              queryParam = `claimNumber=${searchQuery}`
              break
            case 'Nama Member':
              queryParam = `memberName=${searchQuery}`
              break
            case 'Card Number':
              queryParam = `cardNumber=${searchQuery}`
              break
            case 'Policy Number':
              queryParam = `policyNumber=${searchQuery}`
              break
            case 'Customer Group':
              queryParam = `customerGroup=${searchQuery}`
              break
            case 'Created Date':
              queryParam = `createdDate=${searchQuery}`
              break
            case 'Status':
              queryParam = `status=${searchQuery}`
              break
            default:
              queryParam = ''
          }
        }

        console.log('Making request with params:', {
          ProviderCode: auth.currentUser?.providerCode,
          'Page.page': currentPage,
          'Page.pageSize': pageSize,
          'Page.query': queryParam || undefined,
          SearchType: searchType,
          SearchQuery: searchQuery,
        })

        const response = await axios.get(
          `${process.env.REACT_APP_API_PROVIDER_URL}/api/Claims/Settlement`,
          {
            params: {
              ProviderCode: auth.currentUser?.providerCode,
              'Page.page': currentPage,
              'Page.pageSize': pageSize,
              'Page.query': queryParam || undefined,
              SearchType: searchType,
              SearchQuery: searchQuery,
            },
            headers: {
              Authorization: `Bearer ${auth.currentUser?.token}`,
              accept: 'application/json',
              'accept-language': 'en-US,en;q=0.9',
              origin: 'https://id-provider-staging.healthmetrics.com',
              referer: 'https://id-provider-staging.healthmetrics.com/',
              'sec-fetch-dest': 'empty',
              'sec-fetch-mode': 'cors',
              'sec-fetch-site': 'cross-site',
            },
            paramsSerializer: {
              indexes: null,
            },
          }
        )

        console.log('API Response:', response.data)

        if (response.data) {
          console.log('Setting claims:', response.data.items)
          setClaims(response.data.items || [])
          setTotalPages(response.data.totalPages)
          setTotalRecords(response.data.items.length)
        }
      } catch (error: any) {
        console.error('Error fetching claims:', error)

        // Check if error is 401 Unauthorized
        if (error.response && error.response.status === 401) {
          setError('Session expired. Please login again.')
          // Logout user and redirect to login page
          auth.logout()
          navigate('/login')
          return
        }

        setError('Failed to fetch claims')
        setClaims([])
      } finally {
        setLoading(false)
      }
    }

    const timeoutId = setTimeout(() => {
      fetchClaims()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [currentPage, pageSize, auth.currentUser, claimType, searchType, searchQuery, auth, navigate])

  const handlePageChange = (page: number) => {
    onPageChange(page)
  }

  // Debug log for render
  console.log('Current claims:', claims)
  console.log('Current page:', currentPage)
  console.log('Loading state:', loading)

  const updateConfig = () => {
    setConfigLoading(true)
    try {
      LayoutSetup.setConfig(config)
      window.location.reload()
    } catch (error) {
      setConfig(getLayoutFromLocalStorage())
      setConfigLoading(false)
    }
  }

  const reset = () => {
    setResetLoading(true)
    setTimeout(() => {
      setConfig(getLayoutFromLocalStorage())
      setResetLoading(false)
    }, 1000)
  }

  const fetchClaimDetails = async (claimNumber: string) => {
    const apiUrl = `${process.env.REACT_APP_API_PROVIDER_URL}/api/Claims/GetByClaimNumber/${claimNumber}`
    try {
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${auth.currentUser?.token}`,
        },
      })
      console.log('Claim details fetched:', response.data)
      return response.data
    } catch (error) {
      console.error('Error fetching claim details:', error)
      return null
    }
  }

  const handleClaimAction = async (claim: Claim) => {
    console.log(`Processing ${claim.claimNumber}`)
    const claimDetails = await fetchClaimDetails(claim.claimNumber)
    if (claimDetails) {
      console.log('Claim details:', claimDetails)
      if (claimType === 'OUTPATIENT') {
        navigate('/outpatient', {state: {claimDetails}})
      } else {
        navigate('/settlement-detail', {state: {claimDetails}})
      }
    }
  }

  const renderPagination = () => {
    const totalPagesArray = Array.from({length: totalPages}, (_, index) => index + 1)
    const pageLimit = 5
    let startPage = Math.max(1, currentPage - Math.floor(pageLimit / 2))
    let endPage = Math.min(totalPages, startPage + pageLimit - 1)

    if (endPage - startPage < pageLimit - 1) {
      startPage = Math.max(1, endPage - pageLimit + 1)
    }

    const paginationButtonStyle = {
      minWidth: '36px',
      height: '36px',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.95rem',
      fontWeight: '500',
      borderRadius: '6px',
      transition: 'all 0.2s ease',
      border: '1px solid #e9ecef',
      backgroundColor: '#fff',
      color: '#2D3748',
    }

    const activePaginationStyle = {
      ...paginationButtonStyle,
      backgroundColor: '#EBF7F9',
      borderColor: '#059BB6',
      color: '#059BB6',
    }

    return (
      <div className='d-flex gap-2 align-items-center'>
        <button
          className='btn btn-icon'
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={{
            ...paginationButtonStyle,
            opacity: currentPage === 1 ? 0.5 : 1,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          «
        </button>

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            ...paginationButtonStyle,
            opacity: currentPage === 1 ? 0.5 : 1,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          ‹
        </button>

        {startPage > 1 && (
          <>
            <button
              className='btn btn-icon'
              onClick={() => onPageChange(1)}
              style={paginationButtonStyle}
            >
              1
            </button>
            {startPage > 2 && <span style={{color: '#6B7280', padding: '0 4px'}}>...</span>}
          </>
        )}

        {totalPagesArray.slice(startPage - 1, endPage).map((page) => (
          <button
            key={page}
            className='btn btn-icon'
            onClick={() => onPageChange(page)}
            style={currentPage === page ? activePaginationStyle : paginationButtonStyle}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span style={{color: '#6B7280', padding: '0 4px'}}>...</span>
            )}
            <button
              className='btn btn-icon'
              onClick={() => onPageChange(totalPages)}
              style={paginationButtonStyle}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            ...paginationButtonStyle,
            opacity: currentPage === totalPages ? 0.5 : 1,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          }}
        >
          ›
        </button>

        <button
          className='btn btn-icon'
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={{
            ...paginationButtonStyle,
            opacity: currentPage === totalPages ? 0.5 : 1,
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          }}
        >
          »
        </button>
      </div>
    )
  }

  return (
    <StyledCard>
      <SearchContainer>
        <StyledSelect value={searchType} onChange={(e) => onSearchTypeChange(e.target.value)}>
          <option value='Nama Member'>Nama Member</option>
          <option value='Claim Number'>Claim Number</option>
          <option value='Card Number'>Card Number</option>
          <option value='Policy Number'>Policy Number</option>
          <option value='Customer Group'>Customer Group</option>
          <option value='Created Date'>Created Date</option>
          <option value='Status'>Status</option>
        </StyledSelect>

        <StyledInput
          type='text'
          placeholder='Cari...'
          value={searchQuery}
          onChange={(e) => onSearchQueryChange(e.target.value)}
        />
      </SearchContainer>

      {loading ? (
        <div className='d-flex justify-content-center p-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className='alert alert-danger m-5'>{error}</div>
      ) : claims.length === 0 ? (
        <div className='alert alert-info m-5'>No claims found</div>
      ) : (
        <>
          <TableContainer>
            <StyledTable>
              <thead>
                <tr>
                  <th>Name Member</th>
                  <th>Claim Number</th>
                  <th>Card Number</th>
                  <th>Policy Number</th>
                  <th>Customer Group</th>
                  <th>Created Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {claims.map((claim) => (
                  <tr key={claim.id}>
                    <td>{claim.memberName}</td>
                    <td>{claim.claimNumber}</td>
                    <td>{claim.cardNumber}</td>
                    <td>{claim.policyNumber}</td>
                    <td>{claim.customerGroup}</td>
                    <td>{moment(claim.claimDate).format('DD-MM-YYYY')}</td>
                    <td>
                      <StatusBadge $status={claim.status}>{claim.status}</StatusBadge>
                    </td>
                    <td>
                      <ActionButton onClick={() => handleClaimAction(claim)}>
                        <FaEdit />
                      </ActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </TableContainer>

          <div
            className='d-flex justify-content-between align-items-center flex-wrap gap-4 mb-5'
            style={{margin: '0 20px', paddingBottom: '10px'}}
          >
            <div style={{color: '#6B7280', fontSize: '0.95rem'}}>
              {`Showing ${claims.length > 0 ? (currentPage - 1) * pageSize + 1 : 0} to ${Math.min(
                currentPage * pageSize,
                claims.length
              )} of ${claims.length} entries`}
            </div>

            <div className='d-flex gap-2'>{renderPagination()}</div>
          </div>

          {/* <Pagination>
            <PaginationInfo>
              Showing {claims.length > 0 ? ((currentPage - 1) * pageSize) + 1 : 0} to {Math.min(currentPage * pageSize, totalRecords)} of {totalRecords} entries
            </PaginationInfo>
            
            <PaginationButtons>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index + 1}
                  className={currentPage === index + 1 ? 'active' : ''}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </PaginationButtons>
          </Pagination> */}
        </>
      )}
    </StyledCard>
  )
}

export {SettlementPage}
